import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

/** Mirrors `src/types/lensFocalRange.ts` (MOT-452). */
export type LensFocalClass = 'wide' | 'standard' | 'telephoto' | 'superzoom';
export type LensKind = 'prime' | 'zoom';
export type LensFocalSource = 'admin' | 'manufacturer' | 'bhphoto' | 'computed';

export const LENS_FOCAL_CLASSES: LensFocalClass[] = ['wide', 'standard', 'telephoto', 'superzoom'];
export const LENS_KINDS: LensKind[] = ['prime', 'zoom'];
export const LENS_FOCAL_SOURCES: LensFocalSource[] = ['admin', 'manufacturer', 'bhphoto', 'computed'];

export interface LensFocalRange {
  focalClass: LensFocalClass;
  lensKind: LensKind | null;
  source: LensFocalSource;
  basis?: 'name' | 'spec';
  label?: string;
  focalMinMm?: number;
  focalMaxMm?: number;
  equivalentMinMm?: number;
  equivalentMaxMm?: number;
  cropFactor?: number;
  cropBasis?: 'format' | 'equivalent_spec' | 'assumed_full_frame';
  classifiedAt?: string;
}

export interface LensFocalBackfillPage {
  dryRun: boolean;
  scanned: number;
  lenses: number;
  unchanged: number;
  writes: number;
  clears: number;
  unclassified: number;
  bySource: Record<LensFocalSource, number>;
  byClass: Record<LensFocalClass, number>;
  samples: Array<{ id: string; name: string; before: LensFocalRange | null; after: LensFocalRange | null; action: 'write' | 'clear' }>;
  nextCursor: string | null;
  dryRunSession?: { id: string; hash: string; expiresAt: string; changeCount: number };
}

export interface LensFocalProductView {
  productId: string;
  isLens: boolean;
  stored: LensFocalRange | null;
  effective: LensFocalRange | null;
  manufacturer: LensFocalRange | null;
  computed: LensFocalRange | null;
  categoryKey: string | null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await makeApiCall(path, {
    ...init,
    headers: { ...headers, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const message = json?.error?.message || (typeof json?.error === 'string' ? json.error : null) || json?.message || `Request failed (${response.status})`;
    throw new Error(message);
  }
  return json.data as T;
}

export const lensFocalRangeApi = {
  backfillPage: (body: {
    dryRun: boolean;
    cursor?: string | null;
    pageSize?: number;
    dryRunSession?: { id: string; hash: string };
  }) => call<LensFocalBackfillPage>('/admin/lens-focal-range/backfill', { method: 'POST', body: JSON.stringify(body) }),
  getProduct: (productId: string) =>
    call<LensFocalProductView>(`/admin/lens-focal-range/products/${encodeURIComponent(productId)}`),
  setProduct: (productId: string, body: { focalClass: LensFocalClass; lensKind: LensKind | null }) =>
    call<LensFocalProductView>(`/admin/lens-focal-range/products/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  clearProduct: (productId: string) =>
    call<LensFocalProductView>(`/admin/lens-focal-range/products/${encodeURIComponent(productId)}`, { method: 'DELETE' }),
};
